import Typography from '@material-ui/core/Typography'
import { ShoppingCartOutlined } from '@material-ui/icons'
import { DistributorDataTypeV2, HardwareSupplierFilterKeyType } from 'opensolar-common'
import {
  Box,
  Button,
  ChevronRightOutlineIcon,
  InformationCircleOutlinedIcon,
  styled,
  TagOutlineIcon,
  Tooltip,
} from 'opensolar-ui'
import { RefObject, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useHistory } from 'react-router-dom'
import BundleDealModal from './BundleDealModal'
import BundleDealTooltip from './BundleDealTooltip'
import { hasCartConditions, isBundleDeal, isFreeShippingDeal } from './dealEngine'
import { Deal, FreeComponent } from './types'

type DealBadgeVariant = 'light' | 'dark'

const BaseBadge = styled(Typography)(({ theme }) => ({
  padding: 4,
  fontSize: `12px !important`,
  fontWeight: theme.typography.fontWeightMedium,
  borderRadius: 4,
  textTransform: 'uppercase',
  width: 'fit-content',
}))

const DealsAvailableBadge = styled(BaseBadge)<{ badgeVariant?: DealBadgeVariant }>(({ theme }) => ({
  backgroundColor: theme.palette.green[200],
  color: theme.palette.green[1000],
}))

const StyledBxgyBadge = styled(BaseBadge)<{ badgeVariant?: DealBadgeVariant }>(({ theme, badgeVariant }) => ({
  padding: '2px 8px',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  backgroundColor: badgeVariant === 'light' ? '#F3FAFC' : '#007092',
  color: badgeVariant === 'light' ? '#007092' : theme.palette.neutralGrey[100],
}))

const FreeShippingBadge = styled(BaseBadge)<{ badgeVariant?: DealBadgeVariant }>(({ theme, badgeVariant }) => ({
  padding: 4,
  backgroundColor: badgeVariant === 'light' ? theme.palette.green[200] : theme.palette.green[900],
  color: badgeVariant === 'light' ? theme.palette.green[1000] : theme.palette.neutralGrey[100],
}))

const DefaultDealBadge = styled(BaseBadge)(({ theme }) => ({
  padding: 4,
  backgroundColor: theme.palette.green[200],
  color: theme.palette.green[1000],
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}))

const StyledBundleButton = styled(Button)<{ badgeVariant?: DealBadgeVariant }>(({ theme, badgeVariant }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 2,
  padding: '0px 4px !important',
  backgroundColor: `${badgeVariant === 'light' ? '#F1E9FB' : '#6E23C9'} !important`,
  color: `${badgeVariant === 'light' ? '#5C1DA7' : theme.palette.neutralGrey[100]} !important`,
  minHeight: `24px !important`,
  width: 'fit-content !important',
  cursor: 'pointer',
  pointerEvents: 'auto',
}))

const StyledFreeProductLink = styled('span')(({ theme }) => ({
  textDecoration: 'underline',
  cursor: 'pointer',
  color: theme.palette.blue[900],
}))

const BxgyBadge = ({
  buyQuantity,
  freeQuantity,
  freeComponent,
  badgeVariant,
}: {
  buyQuantity: number
  freeQuantity: number
  freeComponent?: FreeComponent
  badgeVariant?: DealBadgeVariant
}) => {
  const translate = useTranslate()
  const history = useHistory()

  const handleFreeProductClick = (event: React.MouseEvent) => {
    if (!freeComponent?.code) return
    event.stopPropagation()
    history.push('/shop/product/' + encodeURIComponent(freeComponent.code))
  }

  const tooltipTitle =
    freeComponent?.name && freeComponent.code ? (
      <span>
        {translate('Buy %{buyQty} get %{freeQty}', { buyQty: buyQuantity, freeQty: freeQuantity })}{' '}
        <StyledFreeProductLink role="link" onClick={handleFreeProductClick}>
          {freeComponent.name}
        </StyledFreeProductLink>{' '}
        {translate('for free')}
      </span>
    ) : (
      `Buy ${buyQuantity} get ${freeQuantity} ${freeComponent?.name ?? ''} for free`
    )

  return (
    <Tooltip interactive enterDelay={0} leaveDelay={200} title={tooltipTitle}>
      <StyledBxgyBadge badgeVariant={badgeVariant}>
        <InformationCircleOutlinedIcon />
        {translate('Bonus Item Offer')}
      </StyledBxgyBadge>
    </Tooltip>
  )
}

const StyledChevronRightOutlineIcon = styled(ChevronRightOutlineIcon)(({ theme }) => ({
  marginRight: 4,
}))

type BundleBadgeProps = {
  deal: Deal
  currentProductCode?: string
  showTooltip?: boolean
  bundleDealModalRef?: RefObject<HTMLDivElement>
  badgeVariant?: DealBadgeVariant
}

const BundleBadge = ({
  deal,
  currentProductCode,
  showTooltip = false,
  bundleDealModalRef,
  badgeVariant,
}: BundleBadgeProps) => {
  const translate = useTranslate()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (showTooltip) return
    setIsModalOpen(true)
  }

  const badge = (
    <StyledBundleButton onClick={handleClick} badgeVariant={badgeVariant}>
      <BaseBadge>{translate('Bundle & Save')}</BaseBadge>
      {showTooltip ? <InformationCircleOutlinedIcon /> : <StyledChevronRightOutlineIcon size={12} />}
    </StyledBundleButton>
  )

  if (showTooltip) {
    return (
      <BundleDealTooltip deal={deal} currentProductCode={currentProductCode}>
        {badge}
      </BundleDealTooltip>
    )
  }

  return (
    <>
      {badge}
      {isModalOpen && (
        <BundleDealModal
          ref={bundleDealModalRef}
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          deal={deal}
          currentProductCode={currentProductCode}
        />
      )}
    </>
  )
}

const DefaultBadge = ({ deal }: { deal: Deal }) => {
  const translate = useTranslate()
  const isCartLevelDeal = hasCartConditions(deal)

  return (
    <Tooltip title={deal.description ?? (isCartLevelDeal ? 'Cart Offer' : 'Deal Offer')}>
      <DefaultDealBadge>
        {isCartLevelDeal ? (
          <>
            <ShoppingCartOutlined fontSize="small" />
            {translate('Cart Offer')}
          </>
        ) : (
          <>
            <TagOutlineIcon size={16} />
            {translate('Deal Offer')}
          </>
        )}
      </DefaultDealBadge>
    </Tooltip>
  )
}

type DealBadgeProps = {
  orderingV2: DistributorDataTypeV2[]
  selectedDistributor?: HardwareSupplierFilterKeyType
  currentProductCode?: string
  /** When true, bundle deals show a hover tooltip instead of opening the selection modal. */
  showTooltip?: boolean
  /** Ref to the bundle modal root — used by ProductTile to ignore tile navigation clicks. */
  bundleDealModalRef?: RefObject<HTMLDivElement>
  variant?: DealBadgeVariant
}

const DealBadge: React.FC<DealBadgeProps> = ({
  orderingV2,
  selectedDistributor,
  currentProductCode,
  showTooltip,
  bundleDealModalRef,
  variant = 'dark',
}) => {
  const translate = useTranslate()

  const deals = useMemo(() => {
    const entries = selectedDistributor
      ? orderingV2.filter((entry) => entry?.distributor === selectedDistributor)
      : orderingV2

    return entries
      .flatMap((entry) => (entry.deals ?? []) as Deal[])
      .filter((deal): deal is Deal => Boolean(deal.id && deal.action))
  }, [orderingV2, selectedDistributor])

  if (!deals.length) return null

  const showDealsAvailableBadge = new Set(deals.map((deal) => deal.distributor).filter(Boolean)).size > 1

  if (showDealsAvailableBadge) {
    return <DealsAvailableBadge badgeVariant={variant}>{translate('Deals Available')}</DealsAvailableBadge>
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={4} width="fit-content">
      {deals.map((deal) => {
        if (!deal?.action) return null
        if (deal.action.discount_type === 'bxgy') {
          return (
            <BxgyBadge
              key={deal.id}
              buyQuantity={deal.action.buy_quantity ?? 0}
              freeQuantity={deal.action.free_quantity ?? 0}
              freeComponent={deal.action.free_component}
              badgeVariant={variant}
            />
          )
        } else if (isFreeShippingDeal(deal)) {
          return (
            <FreeShippingBadge key={deal.id} badgeVariant={variant}>
              {translate('Free Delivery')}
            </FreeShippingBadge>
          )
        } else if (isBundleDeal(deal)) {
          return (
            <BundleBadge
              key={deal.id}
              deal={deal}
              currentProductCode={currentProductCode}
              showTooltip={showTooltip}
              bundleDealModalRef={bundleDealModalRef}
              badgeVariant={variant}
            />
          )
        }
        return <DefaultBadge key={deal.id} deal={deal} />
      })}
    </Box>
  )
}

export default DealBadge
